import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { CartItemSummary, CartService } from '../../../services/cart.service';
import {
  AddpCompanyDetails,
  PopSummary,
  ServiceDeliveryLocationDetails,
  ServiceDeliveryLocationsService,
  ServiceItem
} from '../../../services/service-delivery-locations.service';
import { UtilsService } from '../../../appSharedModule/services/utils.service';

@Component({
  selector: 'app-cart-addp-edit-dialog',
  templateUrl: './cart-addp-edit-dialog.component.html',
  styleUrls: ['./cart-addp-edit-dialog.component.scss']
})
export class CartAddpEditDialogComponent implements OnChanges, OnDestroy {
  @Input() item: CartItemSummary | null = null;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  companyDetails: AddpCompanyDetails | null = null;
  companyDetailsLoading = false;
  companyDetailsError: string | null = null;
  serviceItemsLoading = false;
  serviceItemsError: string | null = null;
  selectedItems: ServiceItem[] = [];
  pop: PopSummary | ServiceDeliveryLocationDetails['commercialPop'] | null = null;
  xcoId = '';
  serviceLabel = 'Purchase an ADDP';
  submitting = false;
  submissionError: string | null = null;
  private payloadSnapshot: any | null = null;

  private companyDetailsSub: Subscription | null = null;
  private locationDetailsSub: Subscription | null = null;
  private submissionSub: Subscription | null = null;

  constructor(
    private readonly service: ServiceDeliveryLocationsService,
    private readonly cartService: CartService,
    private readonly utils: UtilsService
  ) { }

  get initialPayload(): any | null {
    return this.payloadSnapshot;
  }

  get loading(): boolean {
    return this.companyDetailsLoading || this.serviceItemsLoading;
  }

  get canDisplayForm(): boolean {
    return !!(this.companyDetails && this.selectedItems.length && this.pop && this.xcoId && this.payloadSnapshot);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.initializeContext();
      } else {
        this.resetState();
      }
    } else if (changes['item'] && this.visible) {
      this.initializeContext();
    }
  }

  ngOnDestroy(): void {
    this.companyDetailsSub?.unsubscribe();
    this.locationDetailsSub?.unsubscribe();
    this.submissionSub?.unsubscribe();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  handleCancel(): void {
    this.close();
  }

  handleSubmit(payload: any): void {
    if (!this.item) {
      return;
    }
    this.submitting = true;
    this.submissionError = null;
    this.submissionSub?.unsubscribe();
    this.submissionSub = this.cartService.updateItem(this.item.uuid, payload).subscribe({
      next: () => {
        this.submitting = false;
        this.utils.displayMessage('ADDP request updated', 'The cart item was updated successfully.', 'success', false, false, {
          disableCustomClose: true
        });
        this.close();
      },
      error: () => {
        this.submitting = false;
        this.submissionError = 'Unable to update this ADDP request. Please try again later.';
        this.utils.displayMessage('Unable to update ADDP request', this.submissionError, 'error');
      }
    });
  }

  private initializeContext(): void {
    this.submissionError = null;
    this.companyDetailsError = null;
    this.serviceItemsError = null;
    this.selectedItems = [];
    this.companyDetails = null;
    this.pop = null;
    const payload = this.clonePayload(this.item?.payload || null);
    this.payloadSnapshot = payload;

    if (!payload) {
      this.serviceItemsError = 'This cart item has no ADDP payload to edit.';
      return;
    }

    this.xcoId = payload.xco || '';
    this.serviceLabel = payload.service || 'Purchase an ADDP';
    this.pop = payload.pop || null;

    const companyId = payload.company?.id;
    const popId = payload.pop?.id;
    if (!companyId || !popId) {
      this.serviceItemsError = 'Missing company or PoP information for this ADDP.';
      return;
    }

    this.loadCompanyDetails(companyId);
    this.loadServiceItems(companyId, popId, payload);
  }

  private loadCompanyDetails(companyId: number): void {
    this.companyDetailsLoading = true;
    this.companyDetailsError = null;
    this.companyDetailsSub?.unsubscribe();
    this.companyDetailsSub = this.service.getAddpCompanyDetails(companyId).subscribe({
      next: details => {
        this.companyDetails = details;
        this.companyDetailsLoading = false;
      },
      error: () => {
        this.companyDetailsLoading = false;
        this.companyDetailsError = 'Unable to load quote information for this ADDP.';
      }
    });
  }

  private loadServiceItems(companyId: number, popId: number, payload: any): void {
    const selectedIds = new Set<number>();
    if (Array.isArray(payload.items)) {
      payload.items.forEach((entry: any) => {
        if (entry && typeof entry.id === 'number') {
          selectedIds.add(entry.id);
        }
      });
    }
    this.serviceItemsLoading = true;
    this.serviceItemsError = null;
    this.locationDetailsSub?.unsubscribe();
    this.locationDetailsSub = this.service.getLocationDetails(companyId, popId).subscribe({
      next: details => {
        this.pop = details.commercialPop || this.pop;
        this.selectedItems = this.extractServiceItems(details, selectedIds, payload.xco);
        this.serviceItemsLoading = false;
        if (!this.selectedItems.length) {
          this.serviceItemsError = 'Unable to match the products saved in this ADDP.';
        }
      },
      error: () => {
        this.serviceItemsLoading = false;
        this.serviceItemsError = 'Unable to load items for this PoP.';
      }
    });
  }

  private extractServiceItems(
    details: ServiceDeliveryLocationDetails,
    selectedIds: Set<number>,
    xcoId: string | undefined
  ): ServiceItem[] {
    if (!details?.services?.length || selectedIds.size === 0) {
      return [];
    }
    const matches: ServiceItem[] = [];
    details.services.forEach(service => {
      service.items.forEach(item => {
        if (selectedIds.has(item.id) && this.itemMatchesXco(item, xcoId)) {
          matches.push(item);
        }
      });
    });
    return matches;
  }

  private itemMatchesXco(item: ServiceItem, xcoId?: string): boolean {
    if (!xcoId) {
      return true;
    }
    return item.crossConnects?.some(cc => cc.crossConnectId === xcoId) ?? false;
  }

  private clonePayload<T>(payload: T): T | null {
    if (!payload) {
      return null;
    }
    try {
      return JSON.parse(JSON.stringify(payload));
    } catch (e) {
      return payload;
    }
  }

  private resetState(): void {
    this.companyDetailsSub?.unsubscribe();
    this.companyDetailsSub = null;
    this.locationDetailsSub?.unsubscribe();
    this.locationDetailsSub = null;
    this.submissionSub?.unsubscribe();
    this.submissionSub = null;
    this.companyDetailsLoading = false;
    this.companyDetailsError = null;
    this.serviceItemsLoading = false;
    this.serviceItemsError = null;
    this.submissionError = null;
    this.selectedItems = [];
    this.companyDetails = null;
    this.pop = null;
    this.xcoId = '';
    this.payloadSnapshot = null;
  }
}
